import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { formatToBRL } from "@/utils/formatToMoney";
import { Undo2 } from "lucide-react";
import { useState } from "react";
import {
  REVERSAL_LABELS,
  REVERSAL_MIN_REASON_LENGTH,
} from "../../utils/reversal-messages";
import { ReversalEffect } from "../../utils/reversal-effect";

interface ReversalModalProps {
  effect: ReversalEffect;
  isSubmitting?: boolean;
  onConfirm: (reason: string) => void;
}

export function ReversalModal({
  effect,
  isSubmitting,
  onConfirm,
}: ReversalModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [reason, setReason] = useState<string>("");

  const missingCharacters: number = Math.max(
    0,
    REVERSAL_MIN_REASON_LENGTH - reason.trim().length,
  );

  return (
    <div className="w-full max-w-lg space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
          <Undo2 className="h-5 w-5 text-amber-600" />
        </div>
        <p className="font-medium text-gray-900">{REVERSAL_LABELS.TITLE}</p>
      </div>

      <p className="rounded-md bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">
        {effect.statement}
      </p>

      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div className="rounded-md border border-gray-200 p-3">
          <dt className="text-xs text-gray-500">
            {REVERSAL_LABELS.CREDITED_TO_EMPLOYEE}
          </dt>
          <dd className="font-medium text-gray-900">
            {formatToBRL(effect.credited_amount)}
          </dd>
        </div>
        <div className="rounded-md border border-gray-200 p-3">
          <dt className="text-xs text-gray-500">
            {REVERSAL_LABELS.CANCELLED_INSTALLMENTS}
          </dt>
          <dd className="font-medium text-gray-900">
            {formatToBRL(effect.cancelled_amount)}
          </dd>
        </div>
        {effect.registers_loss && (
          <div className="rounded-md border border-gray-200 p-3">
            <dt className="text-xs text-gray-500">
              {REVERSAL_LABELS.LOSS_ON_STORE}
            </dt>
            <dd className="font-medium text-gray-900">
              {formatToBRL(effect.loss_amount)}
            </dd>
          </div>
        )}
      </dl>

      {effect.registers_loss && (
        <p className="text-xs text-gray-600">{REVERSAL_LABELS.LOSS_WARNING}</p>
      )}

      <div className="space-y-1">
        <label htmlFor="reversal-reason" className="text-sm text-gray-700">
          {REVERSAL_LABELS.REASON}
        </label>
        <Textarea
          id="reversal-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="min-h-[90px]"
        />
        {missingCharacters > 0 && (
          <p className="text-xs text-gray-500">
            {REVERSAL_LABELS.REASON_REMAINING(missingCharacters)}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => handleModal({ isOpen: false })}
        >
          {REVERSAL_LABELS.CANCEL}
        </Button>
        <Button
          disabled={missingCharacters > 0 || isSubmitting}
          onClick={() => onConfirm(reason.trim())}
        >
          {REVERSAL_LABELS.CONFIRM}
        </Button>
      </div>
    </div>
  );
}
